/**
 * 通知サービス
 * 注文や呼び出しの通知音・バイブレーションを管理
 */

// 通知音の種類
export type NotificationType = 'order' | 'call' | 'reservation';

// バイブレーションパターン（ミリ秒）
const VIBRATION_PATTERNS: Record<NotificationType, number[]> = {
  order: [200, 100, 200], // 短・休・短
  call: [500, 200, 500, 200, 500], // 長・休・長・休・長（緊急性高い）
  reservation: [300, 100, 300], // 中・休・中
};

class NotificationService {
  private audioContext: AudioContext | null = null;
  private isInitialized = false;
  private isEnabled = true;
  private soundEnabled = true;
  private vibrationEnabled = true;

  /**
   * AudioContextを初期化（ユーザー操作後に呼び出す必要あり）
   */
  initialize(): void {
    if (this.isInitialized) return;

    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      this.isInitialized = true;
      console.log('NotificationService: Initialized');
    } catch (error) {
      console.error('NotificationService: Failed to initialize AudioContext:', error);
    }
  }

  /**
   * AudioContextを再開（ユーザー操作後に呼び出す）
   */
  async resume(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
        console.log('NotificationService: AudioContext resumed');
      } catch (error) {
        console.error('NotificationService: Failed to resume AudioContext:', error);
      }
    }
  }

  /**
   * 通知を有効/無効にする
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * 音声通知を有効/無効にする
   */
  setSoundEnabled(enabled: boolean): void {
    this.soundEnabled = enabled;
  }

  /**
   * バイブレーションを有効/無効にする
   */
  setVibrationEnabled(enabled: boolean): void {
    this.vibrationEnabled = enabled;
  }

  /**
   * 通知を再生
   */
  async notify(type: NotificationType): Promise<void> {
    if (!this.isEnabled) {
      console.log('NotificationService: Notifications disabled');
      return;
    }

    console.log(`NotificationService: Playing ${type} notification`);

    // 音声再生
    if (this.soundEnabled) {
      await this.playSound(type);
    }

    // バイブレーション
    if (this.vibrationEnabled) {
      this.vibrate(type);
    }

    // ブラウザ通知（オプション）
    this.showBrowserNotification(type);
  }

  /**
   * Web Audio APIを使用して通知音を生成・再生
   */
  private async playSound(type: NotificationType): Promise<void> {
    if (!this.audioContext) {
      this.initialize();
    }

    if (!this.audioContext) {
      console.warn('NotificationService: AudioContext not available');
      return;
    }

    // AudioContextがsuspendedの場合は再開
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    const ctx = this.audioContext;
    const now = ctx.currentTime;

    try {
      switch (type) {
        case 'order':
          // 注文: 明るいチャイム音（ドミソの和音）
          this.playChord(ctx, now, [523.25, 659.25, 783.99], 0.5, 0.3);
          break;

        case 'call':
          // 呼び出し: 緊急性の高い連続音
          this.playBeeps(ctx, now, 880, 3, 0.15, 0.1, 0.4);
          break;

        case 'reservation':
          // 予約: 柔らかいベル音
          this.playChord(ctx, now, [587.33, 783.99], 0.4, 0.25);
          break;
      }
    } catch (error) {
      console.error('NotificationService: Failed to play sound:', error);
    }
  }

  /**
   * 和音を再生
   */
  private playChord(
    ctx: AudioContext,
    startTime: number,
    frequencies: number[],
    duration: number,
    volume: number
  ): void {
    frequencies.forEach((freq, index) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(freq, startTime + index * 0.08);

      gainNode.gain.setValueAtTime(volume / frequencies.length, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

      oscillator.start(startTime + index * 0.08);
      oscillator.stop(startTime + duration);
    });
  }

  /**
   * 連続ビープ音を再生
   */
  private playBeeps(
    ctx: AudioContext,
    startTime: number,
    frequency: number,
    count: number,
    beepDuration: number,
    gap: number,
    volume: number
  ): void {
    for (let i = 0; i < count; i++) {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.type = 'square';
      oscillator.frequency.setValueAtTime(frequency, startTime);

      const beepStart = startTime + i * (beepDuration + gap);
      gainNode.gain.setValueAtTime(0, beepStart);
      gainNode.gain.linearRampToValueAtTime(volume, beepStart + 0.01);
      gainNode.gain.setValueAtTime(volume, beepStart + beepDuration - 0.01);
      gainNode.gain.linearRampToValueAtTime(0, beepStart + beepDuration);

      oscillator.start(beepStart);
      oscillator.stop(beepStart + beepDuration);
    }
  }

  /**
   * バイブレーションを実行
   */
  private vibrate(type: NotificationType): void {
    if (!('vibrate' in navigator)) {
      console.log('NotificationService: Vibration API not supported');
      return;
    }

    const pattern = VIBRATION_PATTERNS[type];
    try {
      navigator.vibrate(pattern);
    } catch (error) {
      console.error('NotificationService: Failed to vibrate:', error);
    }
  }

  /**
   * ブラウザ通知を表示
   */
  private async showBrowserNotification(type: NotificationType): Promise<void> {
    if (!('Notification' in window)) {
      return;
    }

    if (Notification.permission !== 'granted') {
      return;
    }

    const titles: Record<NotificationType, string> = {
      order: '新しい注文があります',
      call: 'お客様からの呼び出しです',
      reservation: '新しい予約があります',
    };

    const bodies: Record<NotificationType, string> = {
      order: '注文内容を確認してください',
      call: 'テーブルへお向かいください',
      reservation: '予約内容を確認してください',
    };

    try {
      new Notification(titles[type], {
        body: bodies[type],
        icon: '/logo192.png',
        tag: `notification-${type}`,
        renotify: true,
      });
    } catch (error) {
      console.error('NotificationService: Failed to show browser notification:', error);
    }
  }

  /**
   * ブラウザ通知の許可をリクエスト
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    try {
      return await Notification.requestPermission();
    } catch (error) {
      console.error('NotificationService: Failed to request permission:', error);
      return 'denied';
    }
  }

  /**
   * 注文通知
   */
  async notifyOrder(): Promise<void> {
    await this.notify('order');
  }

  /**
   * 呼び出し通知
   */
  async notifyCall(): Promise<void> {
    await this.notify('call');
  }

  /**
   * 予約通知
   */
  async notifyReservation(): Promise<void> {
    await this.notify('reservation');
  }

  /**
   * テスト用: 通知音を再生
   */
  async testSound(type: NotificationType): Promise<void> {
    console.log(`NotificationService: Testing ${type} sound`);
    // テスト時は有効状態を一時的に無視
    const wasEnabled = this.isEnabled;
    const wasSoundEnabled = this.soundEnabled;
    this.isEnabled = true;
    this.soundEnabled = true;

    await this.playSound(type);
    this.vibrate(type);

    this.isEnabled = wasEnabled;
    this.soundEnabled = wasSoundEnabled;
  }
}

// シングルトンインスタンス
export const notificationService = new NotificationService();
export default notificationService;
